import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  Receipt,
} from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type {
  DocumentCompletenessAssessment,
  DocumentCompletenessIssue,
  DocumentCompletenessIssueType,
  MissingBillIssue,
  MissingRecordIssue,
  ProviderDateRangeMismatch,
} from '@/services/types/case-context-types';

// Simple artifact type for the component - matches what we get from artifacts list
interface ArtifactInfo {
  id: string;
  blob?: { name?: string; download_url?: string } | null;
  artifact_digest?: { title?: string; file_name?: string } | null;
}

interface DocumentCompletenessCardProps {
  assessment: DocumentCompletenessAssessment | null | undefined;
  artifacts?: ArtifactInfo[];
}

// Helper component to render artifact pills
const ArtifactPills: React.FC<{
  artifactIds: string[];
  artifacts?: ArtifactInfo[];
  variant?: 'record' | 'bill';
}> = ({ artifactIds, artifacts, variant = 'record' }) => {
  if (!artifactIds || artifactIds.length === 0) return null;

  const artifactMap = new Map(artifacts?.map((a) => [a.id, a]) || []);

  const colorClasses =
    variant === 'record'
      ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
      : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300';

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {artifactIds.map((artifactId) => {
        const artifact = artifactMap.get(artifactId);
        const displayName = artifact
          ? artifact.blob?.name ||
            artifact.artifact_digest?.title ||
            artifact.artifact_digest?.file_name ||
            `Document ${artifactId.slice(-6)}`
          : `${artifactId.slice(-8)}`;
        const downloadUrl = artifact?.blob?.download_url;

        return downloadUrl ? (
          <a
            key={artifactId}
            href={downloadUrl}
            target="_blank"
            rel="noreferrer noopener"
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border transition-all',
              colorClasses,
            )}
            title={`View: ${displayName}`}
          >
            <FileText className="h-3 w-3" />
            <span className="truncate max-w-[120px]">{displayName}</span>
            <ExternalLink className="h-2.5 w-2.5 opacity-60" />
          </a>
        ) : (
          <span
            key={artifactId}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border',
              colorClasses,
            )}
            title={displayName}
          >
            <FileText className="h-3 w-3" />
            <span className="truncate max-w-[120px]">{displayName}</span>
          </span>
        );
      })}
    </div>
  );
};

const IssueTypeIcon: React.FC<{
  type: DocumentCompletenessIssueType;
  className?: string;
}> = ({ type, className }) => {
  switch (type) {
    case 'date_range_mismatch':
      return <Clock className={cn('h-4 w-4', className)} />;
    case 'missing_bill':
      return <Receipt className={cn('h-4 w-4', className)} />;
    case 'missing_record':
      return <FileText className={cn('h-4 w-4', className)} />;
    default:
      return <AlertCircle className={cn('h-4 w-4', className)} />;
  }
};

const IssueTypeBadge: React.FC<{ type: DocumentCompletenessIssueType }> = ({
  type,
}) => {
  const config = {
    date_range_mismatch: {
      label: 'Date Mismatch',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    missing_bill: {
      label: 'Missing Bill',
      className: 'bg-orange-50 text-orange-700 border-orange-200',
    },
    missing_record: {
      label: 'Missing Record',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
  }[type] || { label: 'Unknown', className: 'bg-gray-50 text-gray-700' };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border',
        config.className,
      )}
    >
      <IssueTypeIcon type={type} className="h-3 w-3" />
      {config.label}
    </span>
  );
};

const DateRangeMismatchCard: React.FC<{ issue: ProviderDateRangeMismatch }> = ({
  issue,
}) => {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 leading-relaxed">
        {issue.description}
      </p>
      <div className="grid grid-cols-2 gap-3">
        {issue.record_date_range && (
          <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100">
            <div className="flex items-center gap-2 mb-1.5">
              <FileText className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">
                Records Date Range
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {issue.record_date_range.start_date || '?'} —{' '}
              {issue.record_date_range.end_date || '?'}
            </p>
          </div>
        )}
        {issue.bill_date_range && (
          <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-100">
            <div className="flex items-center gap-2 mb-1.5">
              <Receipt className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-xs font-medium text-amber-700">
                Bill Date Range
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {issue.bill_date_range.start_date || '?'} —{' '}
              {issue.bill_date_range.end_date || '?'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const MissingBillCard: React.FC<{
  issue: MissingBillIssue;
  artifacts?: ArtifactInfo[];
}> = ({ issue, artifacts }) => {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 leading-relaxed">
        {issue.description}
      </p>
      {issue.record_date_range && (
        <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100 inline-flex items-center gap-3">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">
              Service Dates
            </span>
          </div>
          <p className="text-sm font-medium text-gray-900">
            {issue.record_date_range.start_date || '?'} —{' '}
            {issue.record_date_range.end_date || '?'}
          </p>
        </div>
      )}
      {issue.record_artifact_ids && issue.record_artifact_ids.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">
            Related Records:
          </p>
          <ArtifactPills
            artifactIds={issue.record_artifact_ids}
            artifacts={artifacts}
            variant="record"
          />
        </div>
      )}
    </div>
  );
};

const MissingRecordCard: React.FC<{
  issue: MissingRecordIssue;
  artifacts?: ArtifactInfo[];
}> = ({ issue, artifacts }) => {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 leading-relaxed">
        {issue.description}
      </p>
      <div className="flex flex-wrap gap-3">
        {issue.bill_amount && (
          <div className="bg-green-50/50 rounded-lg p-3 border border-green-100 inline-flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Receipt className="h-3.5 w-3.5 text-green-600" />
              <span className="text-xs font-medium text-green-700">
                Bill Amount
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {issue.bill_amount}
            </p>
          </div>
        )}
        {issue.bill_date_range && (
          <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-100 inline-flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-xs font-medium text-amber-700">
                Bill Dates
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {issue.bill_date_range.start_date || '?'} —{' '}
              {issue.bill_date_range.end_date || '?'}
            </p>
          </div>
        )}
      </div>
      {issue.bill_artifact_ids && issue.bill_artifact_ids.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">
            Related Bills:
          </p>
          <ArtifactPills
            artifactIds={issue.bill_artifact_ids}
            artifacts={artifacts}
            variant="bill"
          />
        </div>
      )}
    </div>
  );
};

const IssueCard: React.FC<{
  issue: DocumentCompletenessIssue;
  artifacts?: ArtifactInfo[];
}> = ({ issue, artifacts }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <IssueTypeBadge type={issue.issue_type} />
          <span className="font-medium text-gray-900">{issue.provider}</span>
          {issue.issue_type === 'date_range_mismatch' &&
            (issue as ProviderDateRangeMismatch).severity === 'error' && (
              <Badge variant="destructive" className="text-[10px] py-0">
                Major Gap
              </Badge>
            )}
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          {issue.issue_type === 'date_range_mismatch' && (
            <DateRangeMismatchCard issue={issue as ProviderDateRangeMismatch} />
          )}
          {issue.issue_type === 'missing_bill' && (
            <MissingBillCard
              issue={issue as MissingBillIssue}
              artifacts={artifacts}
            />
          )}
          {issue.issue_type === 'missing_record' && (
            <MissingRecordCard
              issue={issue as MissingRecordIssue}
              artifacts={artifacts}
            />
          )}
        </div>
      )}
    </div>
  );
};

export const DocumentCompletenessCard: React.FC<
  DocumentCompletenessCardProps
> = ({ assessment, artifacts }) => {
  if (!assessment) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-400">
        <div className="text-center">
          <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No assessment available</p>
        </div>
      </div>
    );
  }

  const hasIssues = assessment.issues.length > 0;
  const completeCount = assessment.providers_with_complete_docs.length;
  const issueCount = assessment.issues.length;

  // Group issues by type
  const dateRangeMismatches = assessment.issues.filter(
    (i) => i.issue_type === 'date_range_mismatch',
  );
  const missingBills = assessment.issues.filter(
    (i) => i.issue_type === 'missing_bill',
  );
  const missingRecords = assessment.issues.filter(
    (i) => i.issue_type === 'missing_record',
  );

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div
          className={cn(
            'rounded-xl p-4 border',
            hasIssues
              ? 'bg-amber-50/50 border-amber-200'
              : 'bg-green-50/50 border-green-200',
          )}
        >
          <div className="flex items-center gap-2 mb-1">
            {hasIssues ? (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            )}
            <span className="text-xs font-medium text-gray-600">Status</span>
          </div>
          <p
            className={cn(
              'text-lg font-semibold',
              hasIssues ? 'text-amber-700' : 'text-green-700',
            )}
          >
            {hasIssues ? 'Action Needed' : 'Complete'}
          </p>
        </div>

        <div className="rounded-xl p-4 bg-green-50/50 border border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-gray-600">Complete</span>
          </div>
          <p className="text-lg font-semibold text-green-700">
            {completeCount}
          </p>
        </div>

        <div className="rounded-xl p-4 bg-orange-50/50 border border-orange-200">
          <div className="flex items-center gap-2 mb-1">
            <Receipt className="h-4 w-4 text-orange-600" />
            <span className="text-xs font-medium text-gray-600">
              Missing Bills
            </span>
          </div>
          <p className="text-lg font-semibold text-orange-700">
            {missingBills.length}
          </p>
        </div>

        <div className="rounded-xl p-4 bg-red-50/50 border border-red-200">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-red-600" />
            <span className="text-xs font-medium text-gray-600">
              Missing Records
            </span>
          </div>
          <p className="text-lg font-semibold text-red-700">
            {missingRecords.length}
          </p>
        </div>
      </div>

      {/* Summary Text */}
      {assessment.summary && (
        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-100">
          {assessment.summary}
        </p>
      )}

      {/* Complete Providers */}
      {completeCount > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            Providers with Complete Documentation
          </h4>
          <div className="flex flex-wrap gap-2">
            {assessment.providers_with_complete_docs.map((provider) => (
              <span
                key={provider}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-green-50 text-green-700 border border-green-200"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {provider}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Issues List */}
      {hasIssues && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Issues Found ({issueCount})
          </h4>

          {dateRangeMismatches.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Date Range Mismatches ({dateRangeMismatches.length})
              </p>
              {dateRangeMismatches.map((issue, idx) => (
                <IssueCard
                  key={`mismatch-${idx}`}
                  issue={issue}
                  artifacts={artifacts}
                />
              ))}
            </div>
          )}

          {missingBills.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Missing Bills ({missingBills.length})
              </p>
              {missingBills.map((issue, idx) => (
                <IssueCard
                  key={`missing-bill-${idx}`}
                  issue={issue}
                  artifacts={artifacts}
                />
              ))}
            </div>
          )}

          {missingRecords.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Missing Records ({missingRecords.length})
              </p>
              {missingRecords.map((issue, idx) => (
                <IssueCard
                  key={`missing-record-${idx}`}
                  issue={issue}
                  artifacts={artifacts}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assessment Timestamp */}
      {assessment.assessed_at && (
        <p className="text-xs text-gray-400 text-right">
          Last assessed:{' '}
          {new Date(assessment.assessed_at).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </p>
      )}
    </div>
  );
};

export default DocumentCompletenessCard;
